import {
  segmentedPillDividerClasses,
  segmentedPillDividerHideOnHoverClasses,
  segmentedPillHoverPillClasses,
} from "@ui/components/internal/SegmentedPill";
import { tv, type VariantProps } from "@ui/lib/tv";
import type { HTMLAttributes, ReactNode } from "react";

/**
 * Segmented pill-shaped bar of trigger items (Where / When / Who -style) with
 * 1px dividers between items and a hover/pressed pill overlay. Layout-only —
 * `PillGroup.Item` provides class strings for any interactive primitive
 * (Button, DialogTrigger child, Link).
 *
 * Mechanics (single-element-per-item, no JS state):
 *   ::before — pill bg (translucent neutral) sized via `bgExtend` variant.
 *   ::after  — 1px divider centered in the next-item gap. Hidden on the
 *               last item; optionally hidden on hover via `divider` variant.
 */
export const pillGroup = tv({
  base: [
    "flex h-15 w-full items-center justify-between overflow-hidden rounded-full bg-white px-3",
    // Drop shadow paints below + sides; keyline is inset so the 1px outline doesn't leak
    // above the bar's top edge (where it would be clipped by a higher-z neighbour like
    // a sticky TopBar).
    "shadow-[var(--shadow-chip),inset_0px_0px_0px_1px_rgba(0,0,0,0.05)]",
  ],
});

export const pillGroupItems = tv({
  base: "flex min-w-0 flex-1 items-center gap-4",
});

export const pillGroupItem = tv({
  base: [
    "group/pillitem relative flex h-15 cursor-pointer items-center px-3 text-left outline-none",
    // Hover bg via ::before — fills the full bar height (inset-y-0) so the
    // pill abuts the bar's rounded edge with matching radius. Use explicit
    // before:-left-N + before:-right-N (not -inset-x-N) so the bgExtend
    // variants can override one side at a time (e.g. last-item right-cap).
    segmentedPillHoverPillClasses,
    "before:rounded-full before:bg-neutral-50 before:duration-150",
    "hover:before:opacity-100 focus-visible:before:opacity-100 data-[pressed]:before:opacity-100",
    "data-[pressed]:before:bg-neutral-100",
    // Right divider via ::after.
    segmentedPillDividerClasses,
    "after:right-[-8.5px] after:h-8 after:bg-neutral-100 after:duration-150",
  ],
  defaultVariants: {
    bgExtend: "enclosing",
    divider: "hide-on-hover",
  },
  variants: {
    /**
     * Where the hover pill ends horizontally.
     *   tight     — bg stays inside the item bounds (ButtonGroup style)
     *   flush     — bg edges sit at the divider centerlines (touches dividers)
     *   enclosing — bg extends past the dividers, dividers (when visible) sit
     *               at the bg's left/right edges
     */
    bgExtend: {
      tight: "before:right-0.5 before:left-0.5",
      flush: "before:-left-2 before:-right-2",
      // Last item caps the right-side bg extension so it doesn't intrude on
      // a trailing sibling (e.g. SearchBar.Filters button living outside the
      // PillGroupItems row). Only applies when bgExtend goes "outside" the
      // item — tight/flush don't need a cap.
      enclosing: "before:-left-3 before:-right-3 last:before:right-0",
    },
    /**
     * Divider visibility during hover/pressed of this OR the next item.
     *   visible      — dividers always render
     *   hide-on-hover — divider hides when its owning item or the next item is
     *                   hovered/pressed (Airbnb / ButtonGroup style)
     */
    divider: {
      visible: "",
      "hide-on-hover": [
        segmentedPillDividerHideOnHoverClasses,
        "data-[pressed]:after:opacity-0 focus-visible:after:opacity-0",
        "has-[+[data-pressed]]:after:opacity-0 has-[+[data-focus-visible]]:after:opacity-0",
      ],
    },
  },
});

export type PillGroupItemVariants = VariantProps<typeof pillGroupItem>;

export interface PillGroupProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/** Outer pill bar — rounded-full white container with shadow. */
export function PillGroup({ children, className, ...props }: PillGroupProps) {
  return (
    <div {...props} className={pillGroup({ className })}>
      {children}
    </div>
  );
}

export interface PillGroupItemsProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/**
 * Layout slot for the item row. Items are flex children with gap-4 spacing;
 * dividers come from `pillGroupItem`'s ::after pseudo. Filters / trailing
 * content lives as a SIBLING of this row, NOT inside it, so it doesn't
 * participate in the divider chain.
 */
export function PillGroupItems({ children, className, ...props }: PillGroupItemsProps) {
  return (
    <div {...props} className={pillGroupItems({ className })}>
      {children}
    </div>
  );
}
